import { Counter } from "@thilke/ui/components/counter";
import { Input } from "@thilke/ui/components/input";
import { cn } from "@thilke/ui/lib/utils";
import type * as React from "react";

type TrackerTone = "default" | "success" | "warning" | "destructive" | "info";

export interface StatFieldProps extends React.ComponentProps<"div"> {
  label: React.ReactNode;
  value: React.ComponentProps<typeof Input>;
  max?: React.ComponentProps<typeof Input>;
  action?: React.ReactNode;
  tone?: TrackerTone;
}

export interface ProvisionItem {
  id: string;
  label?: React.ReactNode;
  checked?: boolean;
  inputProps?: React.ComponentProps<"input">;
}

export interface AttackGridRow {
  id: string;
  name?: React.ReactNode;
  tooltip?: string;
  values: React.ReactNode[];
  inputProps?: React.ComponentProps<typeof Input>[];
}

const trackerToneClass: Record<TrackerTone, string> = {
  default: "border-border",
  success: "border-success/50",
  warning: "border-warning/60",
  destructive: "border-destructive/60",
  info: "border-info/60",
};

const attackColumns = ["1", "2", "3", "4", "5", "6", "7+"];

function StatField({
  label,
  value,
  max,
  action,
  tone = "default",
  className,
  ...props
}: StatFieldProps) {
  return (
    <div
      data-slot="stat-field"
      data-tone={tone}
      className={cn(
        "grid gap-1 rounded-md border bg-card p-2 text-card-foreground",
        trackerToneClass[tone],
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div
          data-slot="stat-field-label"
          className="min-w-0 truncate text-xs font-semibold text-muted-foreground"
        >
          {label}
        </div>
        {action ? (
          <div data-slot="stat-field-action" className="shrink-0">
            {action}
          </div>
        ) : null}
      </div>
      <div
        data-slot="stat-field-controls"
        className={cn(
          "grid items-center gap-1",
          max ? "grid-cols-[1fr_auto_1fr]" : "grid-cols-1",
        )}
      >
        <Input
          inputMode="numeric"
          {...value}
          className={cn("text-center font-mono", value.className)}
        />
        {max ? (
          <>
            <span
              data-slot="stat-field-separator"
              className="text-center text-xs text-muted-foreground"
              aria-hidden="true"
            >
              /
            </span>
            <Input
              inputMode="numeric"
              {...max}
              className={cn("text-center font-mono", max.className)}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}

function InitiativePips({
  value,
  max = 12,
  label = "Initiative",
  className,
  ...props
}: React.ComponentProps<"div"> & {
  value: number;
  max?: number;
  label?: React.ReactNode;
}) {
  return (
    <div
      data-slot="initiative-pips"
      className={cn(
        "rounded-md border border-border bg-card p-2 text-card-foreground",
        className,
      )}
      {...props}
    >
      <Counter label={label} value={value} max={max} tone="accent" size="sm" />
    </div>
  );
}

function ProvisionGrid({
  items,
  columns = 6,
  className,
  ...props
}: React.ComponentProps<"fieldset"> & {
  items: ProvisionItem[];
  columns?: number;
}) {
  const gridTemplateColumns = `repeat(${columns}, minmax(0, 1fr))`;

  return (
    <fieldset
      data-slot="provision-grid"
      className={cn(
        "grid gap-1 rounded-md border border-border p-2",
        className,
      )}
      {...props}
    >
      <div className="grid gap-1" style={{ gridTemplateColumns }}>
        {items.map((item, index) => {
          const label = item.label ?? index + 1;
          return (
            <label
              key={item.id}
              data-slot="provision-grid-item"
              className="grid place-items-center rounded-[calc(var(--radius)-1px)] border border-input bg-background p-1"
            >
              <input
                type="checkbox"
                defaultChecked={item.checked}
                aria-label={
                  typeof label === "string" || typeof label === "number"
                    ? `Provision ${label}`
                    : undefined
                }
                {...item.inputProps}
                className={cn(
                  "size-4 accent-primary",
                  item.inputProps?.className,
                )}
              />
              <span className="sr-only">{label}</span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function AttackGrid({
  rows,
  editable = false,
  showName = true,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  rows: AttackGridRow[];
  editable?: boolean;
  showName?: boolean;
}) {
  return (
    <div
      data-slot="attack-grid"
      className={cn("grid gap-1 overflow-x-auto", className)}
      {...props}
    >
      <div
        className={cn(
          "grid min-w-[22rem] gap-1 text-center text-[11px] font-semibold text-muted-foreground",
          showName
            ? "grid-cols-[minmax(8rem,1fr)_repeat(7,2rem)]"
            : "grid-cols-7",
        )}
      >
        {showName ? (
          <div
            data-slot="attack-grid-header"
            className="rounded-[calc(var(--radius)-2px)] border border-border bg-muted px-1 py-1 text-left leading-none"
          >
            Name
          </div>
        ) : null}
        {attackColumns.map((column) => (
          <div
            key={column}
            data-slot="attack-grid-header"
            className="rounded-[calc(var(--radius)-2px)] border border-border bg-muted px-1 py-1 leading-none"
          >
            {column}
          </div>
        ))}
      </div>
      {rows.map((row) => (
        <div
          key={row.id}
          className={cn(
            "grid min-w-[22rem] items-center gap-1 text-center text-xs",
            showName
              ? "grid-cols-[minmax(8rem,1fr)_repeat(7,2rem)]"
              : "grid-cols-7",
          )}
        >
          {showName ? (
            <div
              data-slot="attack-grid-name"
              title={row.tooltip}
              className="min-w-0 truncate rounded-[calc(var(--radius)-2px)] border border-border bg-background px-2 py-1 text-left font-medium leading-none"
            >
              {row.name}
            </div>
          ) : null}
          {attackColumns.map((column, index) => {
            const inputProps = row.inputProps?.[index];
            return (
              <div
                key={`${row.id}-${column}`}
                data-slot="attack-grid-value"
                className="rounded-[calc(var(--radius)-2px)] border border-border bg-background p-0.5 font-mono leading-none"
              >
                {editable ? (
                  <Input
                    inputMode="numeric"
                    aria-label={
                      typeof row.name === "string" ||
                      typeof row.name === "number"
                        ? `${row.name} ${column}`
                        : `Damage ${column}`
                    }
                    {...inputProps}
                    className={cn(
                      "h-6 px-1 text-center font-mono text-xs",
                      inputProps?.className,
                    )}
                  />
                ) : (
                  row.values[index]
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export {
  AttackGrid,
  attackColumns,
  InitiativePips,
  ProvisionGrid,
  StatField,
  type TrackerTone,
};
